import type { QueryClient } from '@tanstack/react-query';
import type {
  ContractStageUpdatedEvent,
  TaskCollectionChangedNotification,
} from 'types/pushNotifications';
import { emitTaskCollectionRefresh } from 'hooks/useTaskCollectionChanges';

const autopilotTasksQueryRoot = ['getAutopilotTasksForCampaign'] as const;
const staffContractQueryRoot = ['staffGetContract'] as const;

export const invalidateTaskCollection = (
  queryClient: QueryClient,
  event: TaskCollectionChangedNotification
) =>
  queryClient.invalidateQueries({
    queryKey: [...autopilotTasksQueryRoot, event.campaignId, event.contractId],
    exact: true,
  });

type TaskCollectionRefreshEmitter = (event: TaskCollectionChangedNotification) => void;

export const refreshTaskCollection = async (
  queryClient: QueryClient,
  event: TaskCollectionChangedNotification,
  emitRefresh: TaskCollectionRefreshEmitter = emitTaskCollectionRefresh
) => {
  await invalidateTaskCollection(queryClient, event);
  emitRefresh(event);
};

export const invalidateActiveTaskQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({
    queryKey: autopilotTasksQueryRoot,
    type: 'active',
    refetchType: 'active',
  });

export const refreshActiveTaskCollections = async (
  queryClient: QueryClient,
  emitRefresh: TaskCollectionRefreshEmitter = emitTaskCollectionRefresh
) => {
  const activeTaskCollections = queryClient
    .getQueryCache()
    .findAll({
      queryKey: autopilotTasksQueryRoot,
      type: 'active',
    })
    .map(({ queryKey }) => {
      const [, campaignId, contractId] = queryKey;

      if (
        typeof campaignId !== 'string' ||
        (contractId !== undefined && typeof contractId !== 'string')
      ) {
        throw new Error('Unexpected getAutopilotTasksForCampaign query key');
      }

      return { campaignId, contractId };
    });

  await invalidateActiveTaskQueries(queryClient);
  activeTaskCollections.forEach(emitRefresh);
};

export const invalidateCampaignQueriesExceptTasks = (
  queryClient: QueryClient,
  campaignId: string
) =>
  queryClient.invalidateQueries({
    predicate: ({ queryKey }) =>
      queryKey[0] !== autopilotTasksQueryRoot[0] && queryKey.includes(campaignId),
  });

export const invalidateActiveStaffContractQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({
    queryKey: staffContractQueryRoot,
    type: 'active',
    refetchType: 'active',
  });

type ContractInvalidator = (contractId: string) => Promise<unknown> | unknown;
type CampaignInvalidator = (campaignId: string) => Promise<unknown> | unknown;

export const refreshContractStage = (
  event: ContractStageUpdatedEvent,
  invalidateContract: ContractInvalidator,
  invalidateCampaign: CampaignInvalidator
) => {
  if (!event.campaignId || !event.contractId) return;

  return Promise.all([invalidateContract(event.contractId), invalidateCampaign(event.campaignId)]);
};
